import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DocumentMimeKind, OcrWordBox } from '../types';
import { FindHit, PageTextCache } from '../locate/find-engine';
import {
    buildPageCacheFromOcrWords,
    buildPageCacheFromPlainText,
    recognizeImageWithTesseract
} from './tesseract-ocr-provider';

export type OcrOverlayBox = {
    key: string;
    left: number;
    top: number;
    width: number;
    height: number;
    active: boolean;
    soft: boolean;
};

export function useOcr(args: {
    allowOcr?: boolean;
    mimeKind: DocumentMimeKind;
    loading: boolean;
    aligning: boolean;
    displayUrl: string;
    url: string;
    pdfDocRef: React.MutableRefObject<any>;
    pageIndex: number;
    pageCacheRef: React.MutableRefObject<PageTextCache[]>;
    imageRef: React.RefObject<HTMLImageElement>;
    setHasText: (v: boolean) => void;
    setTextReady: (v: boolean) => void;
    setPageCount: (n: number) => void;
    setPageIndex: (n: number) => void;
    onClearFind: () => void;
}) {
    const ocrSeqRef = useRef(0);
    const pendingAutoOcrRef = useRef(false);
    const runningRef = useRef(false);
    const argsRef = useRef(args);
    argsRef.current = args;
    const [ocrRunning, setOcrRunning] = useState(false);
    const [ocrProgress, setOcrProgress] = useState(0);
    const [ocrNote, setOcrNote] = useState('');
    const [ocrBoxes, setOcrBoxes] = useState<OcrWordBox[]>([]);
    const [ocrImageSize, setOcrImageSize] = useState({ w: 0, h: 0 });
    const [ocrPageIndex, setOcrPageIndex] = useState(0);
    const [scanTick, setScanTick] = useState(0);

    const clear = useCallback(() => {
        ocrSeqRef.current += 1;
        pendingAutoOcrRef.current = false;
        runningRef.current = false;
        setOcrRunning(false);
        setOcrProgress(0);
        setOcrNote('');
        setOcrBoxes([]);
        setOcrImageSize({ w: 0, h: 0 });
        setOcrPageIndex(0);
    }, []);

    const resolveImageNaturalSize = useCallback(async (): Promise<{ w: number; h: number }> => {
        const el = args.imageRef.current;
        if (el && el.naturalWidth > 0 && el.naturalHeight > 0) {
            return { w: el.naturalWidth, h: el.naturalHeight };
        }
        const src = args.displayUrl || args.url;
        return new Promise(resolve => {
            const probe = new Image();
            probe.onload = () => resolve({ w: probe.naturalWidth || 0, h: probe.naturalHeight || 0 });
            probe.onerror = () => resolve({ w: 0, h: 0 });
            probe.src = src;
        });
    }, [args.displayUrl, args.url, args.imageRef]);

    const runOcr = async (sourceUrl?: string) => {
        const current = argsRef.current;
        const ocrSource = sourceUrl || current.displayUrl || current.url;

        if (!current.allowOcr || current.mimeKind !== 'image' || !ocrSource || runningRef.current) {
            if (current.mimeKind === 'image' && !ocrSource) {
                setOcrNote('No image to scan');
            }

            return;
        }

        runningRef.current = true;
        const seq = ++ocrSeqRef.current;
        setOcrRunning(true);
        setOcrProgress(0);
        setOcrNote('Preparing…');
        current.onClearFind();
        let result: { status: string; message?: string; text?: string; words?: any[]; imageWidth?: number; imageHeight?: number };
        try {
            result = await recognizeImageWithTesseract(ocrSource, p => {
                if (seq !== ocrSeqRef.current) {
                    return;
                }

                setOcrProgress(p);
                setOcrNote(p < 1 ? `Scanning… ${Math.round(p * 100)}%` : 'Finishing…');
            });
        } catch {
            result = { status: 'failed', message: 'Scan failed' };
        }

        if (seq !== ocrSeqRef.current) {
            return;
        }

        if (result.status !== 'succeeded') {
            current.setHasText(false);
            current.setTextReady(false);
            current.pageCacheRef.current = [];
            runningRef.current = false;
            setOcrRunning(false);
            setOcrBoxes([]);
            setOcrImageSize({ w: 0, h: 0 });
            setOcrNote(result.message || 'Scan failed');
            setScanTick(t => t + 1);
            return;
        }

        const natural = await resolveImageNaturalSize();

        if (seq !== ocrSeqRef.current) {
            return;
        }
        const destW = natural.w;
        const destH = natural.h;
        const srcW = result.imageWidth || 0;
        const srcH = result.imageHeight || 0;
        const canScale = !!(destW && destH && srcW && srcH && (Math.abs(srcW - destW) > 2 || Math.abs(srcH - destH) > 2));
        const scaleX = canScale ? destW / srcW : 1;
        const scaleY = canScale ? destH / srcH : 1;
        const words = (result.words || []).map(w => ({
            text: w.text,
            x0: w.x0 * scaleX,
            y0: w.y0 * scaleY,
            x1: w.x1 * scaleX,
            y1: w.y1 * scaleY
        }));

        if (words.length) {
            const built = buildPageCacheFromOcrWords(0, words);
            current.pageCacheRef.current = [built.cache];
            setOcrBoxes(built.boxes);
        } else {
            current.pageCacheRef.current = [buildPageCacheFromPlainText(0, result.text || '')];
            setOcrBoxes([]);
        }

        setOcrImageSize({
            w: destW || srcW || 0,
            h: destH || srcH || 0
        });

        const cacheText = current.pageCacheRef.current[0] ? current.pageCacheRef.current[0].text : '';
        const ready = cacheText.replace(/\s+/g, '').length > 0;

        current.setHasText(ready);
        current.setTextReady(ready);
        current.setPageCount(1);
        current.setPageIndex(0);
        runningRef.current = false;
        setOcrRunning(false);
        setOcrPageIndex(0);
        setOcrNote(ready ? 'Scan ready' : (result.message || 'No text found'));
        setScanTick(t => t + 1);
    };

    const runPdfPageScan = async () => {
        const current = argsRef.current;
        const pdf = current.pdfDocRef.current;

        if (!current.allowOcr || current.mimeKind !== 'pdf' || !pdf || runningRef.current) {
            if (current.mimeKind === 'pdf' && !pdf) {
                setOcrNote('PDF is still loading');
            }

            return;
        }

        runningRef.current = true;
        const seq = ++ocrSeqRef.current;
        setOcrRunning(true);
        setOcrProgress(0);
        setOcrNote('Preparing page…');
        current.onClearFind();
        let tempUrl = '';
        try {
            const page = await pdf.getPage(current.pageIndex + 1);
            if (seq !== ocrSeqRef.current) {
                return;
            }
            const viewport = page.getViewport({ scale: 2 });
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.floor(viewport.width));
            canvas.height = Math.max(1, Math.floor(viewport.height));
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                runningRef.current = false;
                setOcrNote('Scan failed');
                setOcrRunning(false);
                setScanTick(t => t + 1);
                return;
            }

            const renderTask = page.render({ canvasContext: ctx, viewport } as any);
            await renderTask.promise;

            if (seq !== ocrSeqRef.current) {
                return;
            }

            const blob = await new Promise<Blob | null>(resolve => {
                canvas.toBlob(b => resolve(b), 'image/png');
            });

            if (!blob) {
                runningRef.current = false;
                setOcrNote('Scan failed');
                setOcrRunning(false);
                setScanTick(t => t + 1);
                return;
            }

            tempUrl = URL.createObjectURL(blob);
            const result = await recognizeImageWithTesseract(tempUrl, p => {
                if (seq !== ocrSeqRef.current) {
                    return;
                }

                setOcrProgress(p);
                setOcrNote(p < 1 ? `Scanning… ${Math.round(p * 100)}%` : 'Finishing…');
            });

            if (seq !== ocrSeqRef.current) {
                return;
            }

            if (result.status !== 'succeeded') {
                runningRef.current = false;
                setOcrNote(result.message || 'Scan failed');
                setOcrRunning(false);
                setScanTick(t => t + 1);
                return;
            }

            const pageIndex = current.pageIndex;
            const srcW = result.imageWidth || canvas.width;
            const srcH = result.imageHeight || canvas.height;
            const scaleX = srcW ? canvas.width / srcW : 1;
            const scaleY = srcH ? canvas.height / srcH : 1;
            const words = (result.words || []).map(w => ({
                text: w.text,
                x0: w.x0 * scaleX,
                y0: w.y0 * scaleY,
                x1: w.x1 * scaleX,
                y1: w.y1 * scaleY
            }));
            const pages = current.pageCacheRef.current.slice();

            while (pages.length < pdf.numPages) {
                pages.push(buildPageCacheFromPlainText(pages.length, ''));
            }

            if (words.length) {
                const built = buildPageCacheFromOcrWords(pageIndex, words);
                pages[pageIndex] = built.cache;
                setOcrBoxes(built.boxes);
            } else {
                pages[pageIndex] = buildPageCacheFromPlainText(pageIndex, result.text || '');
                setOcrBoxes([]);
            }

            current.pageCacheRef.current = pages;
            setOcrImageSize({ w: canvas.width, h: canvas.height });
            setOcrPageIndex(pageIndex);

            const ready = pages.some(p => (p.text || '').replace(/\s+/g, '').length > 0);

            current.setHasText(ready);
            current.setTextReady(ready);
            runningRef.current = false;
            setOcrNote(ready ? 'Scan ready' : (result.message || 'No text found'));
            setOcrRunning(false);
            setScanTick(t => t + 1);
        } catch {
            if (seq === ocrSeqRef.current) {
                runningRef.current = false;
                setOcrNote('Scan failed');
                setOcrRunning(false);
                setScanTick(t => t + 1);
            }
        } finally {
            if (tempUrl) {
                try { URL.revokeObjectURL(tempUrl); } catch { }
            }
        }
    };

    const runScan = () => {
        const kind = argsRef.current.mimeKind;

        if (kind === 'image') {
            void runOcr();
            return;
        }

        if (kind === 'pdf') {
            void runPdfPageScan();
            return;
        }

        setOcrNote('Rescan works on images and PDFs');
    };

    const requestAutoOcr = (should: boolean) => {
        pendingAutoOcrRef.current = should;
    };

    useEffect(() => {
        if (!pendingAutoOcrRef.current) {
            return;
        }
        if (args.loading || args.aligning || args.mimeKind !== 'image' || !args.allowOcr || !args.displayUrl) {
            return;
        }
        pendingAutoOcrRef.current = false;
        runOcr(args.displayUrl);
    }, [args.loading, args.aligning, args.mimeKind, args.displayUrl, args.allowOcr]);

    return {
        ocrRunning,
        ocrProgress,
        ocrNote,
        ocrBoxes,
        ocrImageSize,
        ocrPageIndex,
        runScan,
        scanTick,
        clear,
        requestAutoOcr
    };
}

export function buildOcrOverlayBoxes(args: {
    mimeKind: DocumentMimeKind;
    pageIndex: number;
    hits: FindHit[];
    activeIndex: number;
    ocrBoxes: OcrWordBox[];
    ocrImageSize: { w: number; h: number };
    ocrPageIndex: number;
    pageCacheRef: React.MutableRefObject<PageTextCache[]>;
}): OcrOverlayBox[] {
    if ((args.mimeKind !== 'image' && args.mimeKind !== 'pdf') || !args.ocrBoxes.length || !args.hits.length || !args.ocrImageSize.w || !args.ocrImageSize.h) {
        return [];
    }

    if (args.mimeKind === 'pdf' && args.ocrPageIndex !== args.pageIndex) {
        return [];
    }

    const page = args.pageCacheRef.current[args.mimeKind === 'pdf' ? args.pageIndex : 0];
    const out: OcrOverlayBox[] = [];
    const seen: { [k: string]: boolean } = {};

    for (let h = 0; h < args.hits.length; h++) {
        const hit = args.hits[h];

        if (args.mimeKind === 'pdf' && hit.pageIndex !== args.pageIndex) {
            continue;
        }

        const indexSet: { [k: number]: boolean } = {};
        const listed = hit.itemIndices || [];

        for (let i = 0; i < listed.length; i++) {
            indexSet[listed[i]] = true;
        }

        if (page && page.items && page.items.length) {
            for (let i = 0; i < page.items.length; i++) {
                const it = page.items[i];

                if (it.end > hit.start && it.start < hit.end) {
                    indexSet[it.sourceIndex] = true;
                }
            }
        }

        const indices = Object.keys(indexSet).map(k => parseInt(k, 10));

        for (let i = 0; i < indices.length; i++) {
            const idx = indices[i];
            const box = args.ocrBoxes[idx];

            if (!box || box.x1 <= box.x0 || box.y1 <= box.y0) {
                continue;
            }

            const key = idx + ':' + h;

            if (seen[key]) {
                continue;
            }

            seen[key] = true;
            const w = Math.max(box.x1 - box.x0, 1);
            const ht = Math.max(box.y1 - box.y0, 1);
            const padX = Math.max(3, w * 0.08);
            const padY = Math.max(2, ht * 0.22);
            const leftPx = Math.max(0, box.x0 - padX);
            const topPx = Math.max(0, box.y0 - padY);
            const rightPx = Math.min(args.ocrImageSize.w, box.x1 + padX);
            const bottomPx = Math.min(args.ocrImageSize.h, box.y1 + padY);

            out.push({
                key,
                left: (leftPx / args.ocrImageSize.w) * 100,
                top: (topPx / args.ocrImageSize.h) * 100,
                width: ((rightPx - leftPx) / args.ocrImageSize.w) * 100,
                height: ((bottomPx - topPx) / args.ocrImageSize.h) * 100,
                active: h === args.activeIndex,
                soft: !!hit.soft
            });
        }
    }

    return out;
}
